import { useMemo, useState } from 'react';
import { useValidator } from '../../../utils/hooks';
import { IFormData, SupportFormOption, SupportFormOptionLabels } from '../cus-support-form.data';
import * as Yup from 'yup';
import { ICusSupportFormConfig } from '../cus-support-form.props.autogenerated';
import { PHONE_REGEX } from '../../../const/regex';

const MIN_ITEMS_REQUIRED = 0;

const formDataInitial: IFormData = {
    name: null,
    email: null,
    description: null,
    orderNumber: null,
    isLoading: false,
    phoneNumber: null,
    size: null,
    color: null,
    itemNumber: null,
    topic: SupportFormOption.MissingItem,
    items: []
};

const formSchemaProvider = (config: ICusSupportFormConfig) =>
    Yup.object({
        name: Yup.string().required(config.nameField?.errorMessage),
        email: Yup.string()
            .email(config.emailField?.errorMessage)
            .required(config.emailField?.errorMessage),
        description: Yup.string().required(config.descriptionField?.errorMessage),
        phoneNumber: Yup.string()
            .required(config.phoneNumber?.errorMessage)
            .matches(PHONE_REGEX, config.phoneNumber?.errorFormatMessage),

        orderNumber: Yup.string().when('topic', {
            is: (val: any) =>
                [
                    SupportFormOption.OrderNotReceived,
                    SupportFormOption.WrongSizeColor,
                    SupportFormOption.DamagedItem,
                    SupportFormOption.MissingItem
                ].includes(val),
            then: schema =>
                schema
                    .required(config.orderNumberField?.errorMessage)
                    .matches(/^(?!OL)(?!ol).*/, config.orderNumberField?.errorMessageWrongStore),
            otherwise: schema => schema.nullable()
        }),
        items: Yup.array().when('topic', {
            is: (val: any) => [SupportFormOption.WrongSizeColor, SupportFormOption.DamagedItem].includes(val),
            then: schema => schema.min(MIN_ITEMS_REQUIRED, config.itemsField?.errorMessage),
            otherwise: schema => schema.nullable()
        })
    });

export default function useForm(config: ICusSupportFormConfig) {
    const [formData, setFormData] = useState<IFormData>(formDataInitial);
    const formSchema = useMemo(() => formSchemaProvider(config), [config]);

    const { errors, isValid } = useValidator(formSchema, formData);
    const selectOptions = () => {
        if (config.itemsLabels) {
            const labels = SupportFormOptionLabels(config.itemsLabels);

            return Object.entries(labels).map(([value, label]) => ({
                value,
                label
            }));
        }
        return [];
    };
    const updateFormData = (field: keyof IFormData, value: typeof formData[keyof IFormData]) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    };

    const clearFormData = () => {
        setFormData(formDataInitial);
    };

    const updateFormItems = (items: IFormData['items']) => {
        setFormData(prevData => ({
            ...prevData,
            items: items
        }));
    };

    const updateFormItemData = (index: number, field: string, value: any) => {
        const updatedItems = [...formData.items];
        (updatedItems[index] as any)[field] = value;
        updateFormData('items', updatedItems);
    };

    return {
        formData,
        updateFormData,
        selectOptions: selectOptions(),
        errors,
        isValid,
        updateFormItems,
        updateFormItemData,
        clearFormData
    };
}
